import LmsTitleDescriptionFields from '../../components/LmsTitleDescriptionFields';
import LmsEditorHeader from '../../components/LmsEditorHeader';
import FormActionButtons from '../../components/FormActionButtons';
import { PageLoader } from '../../components/PageLoader';
import { useState, useEffect, useRef } from 'react';
import {
  Loader2,
  Plus,
  Trash2,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Image,
  FileText,
  Video,
  HelpCircle,
  Type,
  Upload,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  Eye,
} from 'lucide-react';
import { uploadAPI, lmsAPI } from '../../services/api';
import { useEditFormPageSetup } from '../../hooks/useEditFormPageSetup';
import type { LmsModulePreviewLocationState } from './LmsModulePreviewPage';

type SlideType = 'text' | 'image' | 'video' | 'pdf' | 'quiz_single' | 'quiz_multiple' | 'quiz_truefalse';

interface QuizOption {
  id: string;
  text: string;
  is_correct: boolean;
}

interface Slide {
  id: string;
  slide_type: SlideType;
  order_index: number;
  title: string;
  body_text: string;
  media_url: string;
  video_embed_url: string;
  quiz_question: string;
  quiz_options: QuizOption[];
  quiz_explanation: string;
}

interface ModuleForm {
  title: string;
  description: string;
  thumbnail_url: string;
  status: 'draft' | 'published' | 'archived';
  points_reward: number;
  pass_score: number;
  estimated_duration_minutes: number;
  quiz_time_limit_minutes: number;
  /** "Complete within N days". 0 = no due date. */
  due_within_days: number;
  /** Minimum reading time, in minutes (halves allowed). 0 = no minimum. */
  min_time_minutes: number;
  /** Whether this module can be attached to a learning journey stage. */
  available_in_journey: boolean;
}

/** The longest minimum reading time the server accepts (10 hours). */
const MIN_TIME_MAX_MINUTES = 600;

/** Saved seconds as the minutes the form shows; 0 when there is no minimum. */
const secondsToMinutes = (seconds: unknown): number => {
  const n = Number(seconds);
  return Number.isFinite(n) && n > 0 ? Math.round((n / 60) * 100) / 100 : 0;
};

const slideTypeConfig: Record<SlideType, { label: string; icon: React.ComponentType<{ className?: string }>; color: string }> = {
  text: { label: 'Text', icon: Type, color: 'bg-blue-100 text-blue-600' },
  image: { label: 'Image', icon: Image, color: 'bg-green-100 text-green-600' },
  video: { label: 'Video', icon: Video, color: 'bg-purple-100 text-purple-600' },
  pdf: { label: 'PDF', icon: FileText, color: 'bg-orange-100 text-orange-600' },
  quiz_single: { label: 'Single Choice', icon: HelpCircle, color: 'bg-yellow-100 text-yellow-600' },
  quiz_multiple: { label: 'Multiple Choice', icon: HelpCircle, color: 'bg-yellow-100 text-yellow-600' },
  quiz_truefalse: { label: 'True/False', icon: HelpCircle, color: 'bg-yellow-100 text-yellow-600' },
};

export default function LmsContentEditorPage() {
  const { id, navigate, isEditing, projectId, loading, setLoading, saving, setSaving } =
    useEditFormPageSetup();
  const [moduleForm, setModuleForm] = useState<ModuleForm>({
    title: '',
    description: '',
    thumbnail_url: '',
    status: 'draft',
    points_reward: 10,
    pass_score: 70,
    estimated_duration_minutes: 5,
    available_in_journey: true,
    quiz_time_limit_minutes: 0,
    due_within_days: 0,
    // A new module starts with half its duration as the minimum reading time. A
    // module that already exists shows what was saved for it: none, unless an
    // admin has set one.
    min_time_minutes: isEditing ? 0 : 2.5,
  });
  // Until the admin types a minimum of their own, a new module's follows its duration.
  const [minTimeFollowsDuration, setMinTimeFollowsDuration] = useState(!isEditing);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [isScorm, setIsScorm] = useState(false);
  const [expandedSlide, setExpandedSlide] = useState<string | null>(null);
  const [dragSlideId, setDragSlideId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [showAddSlide, setShowAddSlide] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingSlideMedia, setUploadingSlideMedia] = useState<string | null>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const slideMediaInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // The form and slides as last loaded from the server, to tell whether the Preview
  // (which shows the saved module) would miss unsaved edits.
  const savedSnapshotRef = useRef<string | null>(null);

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG)');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }

    try {
      setUploadingThumbnail(true);
      const response = await uploadAPI.uploadFile(file, 'lms/thumbnails');
      // API response structure: { success: true, data: { fileName, blobPath, url } }
      const data = response.data?.data;
      if (data?.blobPath) {
        // Always use the proxy URL - direct Azure URLs require authentication
        const thumbnailUrl = `/api/images/view/${data.blobPath}`;
        // Functional update: this runs after the upload's await, so `moduleForm` from
        // before it is stale and would undo any field edited during the upload.
        setModuleForm(prev => ({ ...prev, thumbnail_url: thumbnailUrl }));
      } else {
        console.error('Unexpected response format:', response.data);
        alert('Upload succeeded but could not get URL. Please try again.');
      }
    } catch (err) {
      console.error('Error uploading thumbnail:', err);
      alert('Failed to upload thumbnail. Please try again.');
    } finally {
      setUploadingThumbnail(false);
      // Reset the input so the same file can be selected again
      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      }
    }
  };

  const handleSlideMediaUpload = async (slideId: string, file: File, mediaType: 'image' | 'pdf') => {
    // Validate file type
    if (mediaType === 'image' && !file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG)');
      return;
    }
    if (mediaType === 'pdf' && file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }

    // Validate file size (20MB max for PDFs, 10MB for images)
    const maxSizeMB = mediaType === 'pdf' ? 20 : 10;
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    try {
      setUploadingSlideMedia(slideId);
      const folder = mediaType === 'image' ? 'lms/slides/images' : 'lms/slides/pdfs';
      const response = await uploadAPI.uploadFile(file, folder);
      // API response structure: { success: true, data: { fileName, blobPath, url } }
      const data = response.data?.data;
      if (data?.blobPath) {
        // Always use the proxy URL - direct Azure URLs require authentication
        const mediaUrl = `/api/images/view/${data.blobPath}`;
        updateSlide(slideId, { media_url: mediaUrl });
      } else {
        console.error('Unexpected response format:', response.data);
        alert('Upload succeeded but could not get URL. Please try again.');
      }
    } catch (err) {
      console.error('Error uploading slide media:', err);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingSlideMedia(null);
      // Reset the input
      const inputRef = slideMediaInputRefs.current[slideId];
      if (inputRef) {
        inputRef.value = '';
      }
    }
  };

  // Fetch module if editing
  useEffect(() => {
    const fetchModule = async () => {
      if (isEditing && id) {
        try {
          const response = await lmsAPI.getModuleById(id, projectId);
          const data = response.data?.data;
          if (data) {
            setIsScorm(data.module?.module_type === 'scorm');
            const loadedForm: ModuleForm = {
              title: data.module.title || '',
              description: data.module.description || '',
              thumbnail_url: data.module.thumbnail_url || '',
              status: data.module.status || 'draft',
              points_reward: data.module.points_reward || 10,
              pass_score: data.module.pass_score || 70,
              estimated_duration_minutes: data.module.estimated_duration_minutes || 5,
              available_in_journey: data.module.available_in_journey !== false,
              quiz_time_limit_minutes: data.module.quiz_time_limit_minutes ?? 0,
              due_within_days: data.module.due_within_days ?? 0,
              min_time_minutes: secondsToMinutes(data.module.min_time_seconds),
            };
            setModuleForm(loadedForm);
            // Map slides from DB format to component format
            const mappedSlides = (data.slides || []).map((s: any, index: number) => ({
              id: s.id?.toString() || `slide-${index}`,
              slide_type: s.slide_type,
              order_index: s.display_order || index,
              title: s.title || '',
              body_text: s.body_text || '',
              media_url: s.media_url || '',
              video_embed_url: s.video_embed_url || '',
              quiz_question: s.quiz_question || '',
              quiz_options: s.quiz_options || [],
              quiz_explanation: s.quiz_explanation || '',
            }));
            setSlides(mappedSlides);
            savedSnapshotRef.current = JSON.stringify({ form: loadedForm, slides: mappedSlides });
          }
        } catch (err) {
          console.error('Error fetching module:', err);
          alert('Failed to load module data');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchModule();
  }, [id, isEditing, projectId]);

  const handleSave = async () => {
    if (!moduleForm.title.trim()) {
      alert('Module title is required');
      return;
    }

    try {
      setSaving(true);

      // Prepare slides data for API
      const slidesData = slides.map((slide, index) => ({
        // Lets the server keep the stored row (and learners' quiz answers on it) for a
        // slide that only moved. Ids it doesn't recognise, like temp-…, get new rows.
        id: slide.id,
        slide_type: slide.slide_type,
        title: slide.title,
        body_text: slide.body_text,
        media_url: slide.media_url,
        video_embed_url: slide.video_embed_url,
        quiz_question: slide.quiz_question,
        quiz_options: slide.quiz_options,
        quiz_explanation: slide.quiz_explanation,
        display_order: index,
      }));

      const { min_time_minutes, ...moduleFields } = moduleForm;
      const moduleData = {
        ...moduleFields,
        // Stored in seconds. 0 = no minimum.
        min_time_seconds: Math.round(min_time_minutes * 60),
        slides: slidesData,
      };

      if (isEditing && id) {
        await lmsAPI.updateModule(id, moduleData, projectId);
      } else {
        await lmsAPI.createModule(moduleData, projectId);
      }

      navigate('/lms/content');
    } catch (err: any) {
      console.error('Error saving module:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Unknown error';
      alert(`Failed to save module: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const addSlide = (type: SlideType) => {
    const newSlide: Slide = {
      id: `temp-${Date.now()}`,
      slide_type: type,
      order_index: slides.length,
      title: '',
      body_text: '',
      media_url: '',
      video_embed_url: '',
      quiz_question: '',
      quiz_options: type === 'quiz_truefalse'
        ? [
            { id: '1', text: 'True', is_correct: false },
            { id: '2', text: 'False', is_correct: false },
          ]
        : [
            { id: '1', text: '', is_correct: false },
            { id: '2', text: '', is_correct: false },
          ],
      quiz_explanation: '',
    };
    setSlides([...slides, newSlide]);
    setExpandedSlide(newSlide.id);
    setShowAddSlide(false);
  };

  // Functional update: media uploads call this after an await, and a `slides`
  // snapshot from before the upload would undo any reorder or edit made meanwhile.
  const updateSlide = (slideId: string, updates: Partial<Slide>) => {
    setSlides(prev => prev.map(s => s.id === slideId ? { ...s, ...updates } : s));
  };

  // Reorder is local until Save: handleSave writes display_order from array
  // position, and the mobile app reads slides back ORDER BY display_order.
  // The target index is resolved against the latest list, not the render snapshot.
  const moveSlide = (slideId: string, targetIndex: (list: Slide[]) => number) => {
    setSlides(prev => {
      const from = prev.findIndex(s => s.id === slideId);
      const to = targetIndex(prev);
      if (from < 0 || to < 0 || to >= prev.length || from === to) return prev;
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const indexOfSlide = (list: Slide[], slideId: string) => list.findIndex(s => s.id === slideId);

  const endSlideDrag = () => {
    setDragSlideId(null);
    setDropTargetId(null);
  };

  const removeSlide = (slideId: string) => {
    if (confirm('Are you sure you want to remove this slide?')) {
      setSlides(prev => prev.filter(s => s.id !== slideId));
    }
  };

  const addQuizOption = (slideId: string) => {
    const slide = slides.find(s => s.id === slideId);
    if (slide) {
      updateSlide(slideId, {
        quiz_options: [
          ...slide.quiz_options,
          { id: `${Date.now()}`, text: '', is_correct: false },
        ],
      });
    }
  };

  const updateQuizOption = (slideId: string, optionId: string, updates: Partial<QuizOption>) => {
    const slide = slides.find(s => s.id === slideId);
    if (slide) {
      updateSlide(slideId, {
        quiz_options: slide.quiz_options.map(o => o.id === optionId ? { ...o, ...updates } : o),
      });
    }
  };

  const removeQuizOption = (slideId: string, optionId: string) => {
    const slide = slides.find(s => s.id === slideId);
    if (slide && slide.quiz_options.length > 2) {
      updateSlide(slideId, {
        quiz_options: slide.quiz_options.filter(o => o.id !== optionId),
      });
    }
  };

  // Preview shows the module as last saved. Say so first when there are unsaved edits,
  // since leaving the editor also discards them.
  const openPreview = () => {
    const unsaved =
      savedSnapshotRef.current !== null &&
      savedSnapshotRef.current !== JSON.stringify({ form: moduleForm, slides });
    if (
      unsaved &&
      !window.confirm(
        'You have unsaved changes. The preview shows the last saved version of this module, and your unsaved changes will be lost if you leave this page. Open the preview anyway?'
      )
    ) {
      return;
    }
    const state: LmsModulePreviewLocationState = { from: 'editor' };
    navigate(`/lms/content/${id}/preview`, { state });
  };

  if (loading) {
    return (
      <PageLoader />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <LmsEditorHeader
        onBack={() => navigate('/lms/content')}
        title={isEditing ? 'Edit Module' : 'Create Module'}
        subtitle="Build your training content with slides"
        actions={
          <div className="flex items-center gap-3">
            {isEditing && id && !isScorm && (
              <button
                type="button"
                onClick={openPreview}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
            )}
            <FormActionButtons
              onCancel={() => navigate('/lms/content')}
              onSave={handleSave}
              saving={saving}
              saveLabel="Save Module"
            />
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Module Details */}
        <div className="lg:col-span-1 space-y-6">
          <div className="rounded-lg border border-gray-100 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Module Details</h2>

            <div className="space-y-4">
              <LmsTitleDescriptionFields
                title={moduleForm.title}
                onTitleChange={(value) => setModuleForm({ ...moduleForm, title: value })}
                titlePlaceholder="Enter module title"
                description={moduleForm.description}
                onDescriptionChange={(value) => setModuleForm({ ...moduleForm, description: value })}
                descriptionPlaceholder="Describe what this module covers"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail</label>
                <input
                  type="file"
                  ref={thumbnailInputRef}
                  onChange={handleThumbnailUpload}
                  accept="image/png,image/jpeg,image/jpg"
                  className="hidden"
                />
                {moduleForm.thumbnail_url ? (
                  <div className="relative group">
                    <img
                      src={moduleForm.thumbnail_url}
                      alt="Module thumbnail"
                      className="w-full h-40 object-cover rounded-lg border border-gray-200"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => thumbnailInputRef.current?.click()}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                      >
                        Change
                      </button>
                      <button
                        type="button"
                        onClick={() => setModuleForm({ ...moduleForm, thumbnail_url: '' })}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => !uploadingThumbnail && thumbnailInputRef.current?.click()}
                    className={`border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-400 transition-colors cursor-pointer ${uploadingThumbnail ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    {uploadingThumbnail ? (
                      <Loader2 className="w-8 h-8 mx-auto text-primary-500 mb-2 animate-spin" />
                    ) : (
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    )}
                    <p className="text-sm text-gray-500">
                      {uploadingThumbnail ? 'Uploading...' : 'Click to upload thumbnail'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 2MB</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Points Reward</label>
                  <input
                    type="number"
                    value={moduleForm.points_reward}
                    onChange={(e) => setModuleForm({ ...moduleForm, points_reward: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pass Score (%)</label>
                  <input
                    type="number"
                    value={moduleForm.pass_score}
                    onChange={(e) => setModuleForm({ ...moduleForm, pass_score: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min={0}
                    max={100}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                <input
                  type="number"
                  value={moduleForm.estimated_duration_minutes}
                  onChange={(e) => {
                    const minutes = parseInt(e.target.value) || 0;
                    setModuleForm({
                      ...moduleForm,
                      estimated_duration_minutes: minutes,
                      ...(minTimeFollowsDuration ? { min_time_minutes: Math.min(MIN_TIME_MAX_MINUTES, minutes / 2) } : {}),
                    });
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  min={1}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quiz time limit (minutes)</label>
                <input
                  type="number"
                  value={moduleForm.quiz_time_limit_minutes}
                  onChange={(e) => setModuleForm({ ...moduleForm, quiz_time_limit_minutes: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  min={0}
                />
                <p className="text-xs text-gray-400 mt-1">Applies to quizzes only. 0 = no time limit.</p>
              </div>

              {!isScorm && (
                <div>
                  <label htmlFor="module-min-time-minutes" className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum time (minutes)
                  </label>
                  <input
                    id="module-min-time-minutes"
                    type="number"
                    value={moduleForm.min_time_minutes || ''}
                    onChange={(e) => {
                      setMinTimeFollowsDuration(false);
                      setModuleForm({
                        ...moduleForm,
                        min_time_minutes: Math.min(MIN_TIME_MAX_MINUTES, Math.max(0, parseFloat(e.target.value) || 0)),
                      });
                    }}
                    placeholder="No minimum"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min={0}
                    max={MIN_TIME_MAX_MINUTES}
                    step={0.5}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Leave blank for no minimum. The app keeps each slide open for its share of this time, longer
                    slides for longer, before the learner can move on, so the module cannot be completed sooner.
                    Quiz questions are not held. A new module starts at half its duration.
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="module-due-within-days" className="block text-sm font-medium text-gray-700 mb-1">
                  Complete within (days)
                </label>
                <input
                  id="module-due-within-days"
                  type="number"
                  value={moduleForm.due_within_days || ''}
                  onChange={(e) =>
                    setModuleForm({
                      ...moduleForm,
                      due_within_days: Math.min(365, Math.max(0, parseInt(e.target.value) || 0)),
                    })
                  }
                  placeholder="No due date"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  min={0}
                  max={365}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Leave blank for no due date. Employees already in the project get this many days from the day the
                  module is published. Anyone who joins later gets them from their joining date.
                </p>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={moduleForm.available_in_journey}
                    onChange={(e) => setModuleForm({ ...moduleForm, available_in_journey: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Available in learning journeys</span>
                </label>
                <p className="text-xs text-gray-400 mt-1">
                  Uncheck to keep this module out of the journey builder. Stages that already use it are unaffected.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Slides Builder */}
        <div className="lg:col-span-2">
          {isScorm ? (
            <div className="rounded-lg border border-gray-100 bg-white p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">SCORM Package</h2>
              <p className="text-sm text-gray-500">
                This module's content comes from an imported SCORM package and can't be edited here.
                You can still update the title, thumbnail, points, assign it, and publish it. To change the
                content, re-import an updated package.
              </p>
              <button
                type="button"
                onClick={() => navigate(`/lms/content/${id}/preview`)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50"
              >
                Preview package
              </button>
            </div>
          ) : (
          <div className="rounded-lg border border-gray-100 bg-white p-6">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Slides ({slides.length})</h2>
                {slides.length > 1 && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Drag a slide or use its arrows to reorder. The new order applies when you save.
                  </p>
                )}
              </div>
              <div className="relative">
                <button
                  onClick={() => setShowAddSlide(!showAddSlide)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50"
                >
                  <Plus className="w-4 h-4" />
                  Add Slide
                  <ChevronDown className={`w-4 h-4 transition-transform ${showAddSlide ? 'rotate-180' : ''}`} />
                </button>

                {showAddSlide && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    {(Object.entries(slideTypeConfig) as [SlideType, typeof slideTypeConfig[SlideType]][]).map(([type, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={type}
                          onClick={() => addSlide(type)}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          <span className={`w-6 h-6 rounded flex items-center justify-center ${config.color}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </span>
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {slides.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No slides yet</h3>
                <p className="text-gray-500 mb-4">Add slides to build your training content</p>
                <button
                  onClick={() => setShowAddSlide(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50"
                >
                  <Plus className="w-4 h-4" />
                  Add your first slide
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {slides.map((slide, index) => {
                  const config = slideTypeConfig[slide.slide_type];
                  const Icon = config.icon;
                  const isExpanded = expandedSlide === slide.id;
                  const isDropTarget = dragSlideId !== null && dropTargetId === slide.id && dragSlideId !== slide.id;

                  return (
                    <div
                      key={slide.id}
                      // Only intercept slide drags (dragSlideId set) so dragging text
                      // into the inputs of an expanded slide keeps working normally.
                      onDragOver={(e) => {
                        if (!dragSlideId) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dropTargetId !== slide.id) setDropTargetId(slide.id);
                      }}
                      onDragLeave={(e) => {
                        // Clear the highlight once the pointer leaves the card (e.g. into the
                        // gap between cards, where a drop does nothing).
                        if (dropTargetId === slide.id && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
                          setDropTargetId(null);
                        }
                      }}
                      onDrop={(e) => {
                        if (!dragSlideId) return;
                        e.preventDefault();
                        moveSlide(dragSlideId, list => indexOfSlide(list, slide.id));
                        endSlideDrag();
                      }}
                      className={`border rounded-lg overflow-hidden transition-shadow ${
                        isDropTarget ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200'
                      } ${dragSlideId === slide.id ? 'opacity-50' : ''}`}
                    >
                      {/* Slide Header */}
                      <div
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move';
                          // Firefox won't start a drag unless some data is set. Use a custom type,
                          // not text/plain, or releasing over a text field (e.g. the module Title)
                          // pastes the slide id into it.
                          e.dataTransfer.setData('application/x-lms-slide', slide.id);
                          setDragSlideId(slide.id);
                        }}
                        onDragEnd={endSlideDrag}
                        className="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedSlide(isExpanded ? null : slide.id)}
                      >
                        <span title="Drag to reorder">
                          <GripVertical className="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing" />
                        </span>
                        <span className="w-6 h-6 rounded bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-semibold">
                          {index + 1}
                        </span>
                        <span className={`w-6 h-6 rounded flex items-center justify-center ${config.color}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </span>
                        <span className="flex-1 text-sm font-medium text-gray-900">
                          {slide.title || `${config.label} Slide`}
                        </span>
                        {/* Keyboard/touch alternative to dragging. The wrapper swallows clicks so
                            pressing an arrow (even a disabled one) doesn't toggle the slide open. */}
                        <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => moveSlide(slide.id, list => indexOfSlide(list, slide.id) - 1)}
                            disabled={index === 0}
                            title="Move up"
                            aria-label={`Move slide ${index + 1} up`}
                            className="p-1 text-gray-400 hover:text-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-gray-400"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveSlide(slide.id, list => indexOfSlide(list, slide.id) + 1)}
                            disabled={index === slides.length - 1}
                            title="Move down"
                            aria-label={`Move slide ${index + 1} down`}
                            className="p-1 text-gray-400 hover:text-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-gray-400"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSlide(slide.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </div>

                      {/* Slide Content */}
                      {isExpanded && (
                        <div className="p-4 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Slide Title</label>
                            <input
                              type="text"
                              value={slide.title}
                              onChange={(e) => updateSlide(slide.id, { title: e.target.value })}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="Enter slide title"
                            />
                          </div>

                          {/* Content based on slide type */}
                          {slide.slide_type === 'text' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                              <textarea
                                value={slide.body_text}
                                onChange={(e) => updateSlide(slide.id, { body_text: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                                rows={6}
                                placeholder="Enter slide content..."
                              />
                            </div>
                          )}

                          {slide.slide_type === 'image' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                              <input
                                type="file"
                                ref={(el) => { slideMediaInputRefs.current[slide.id] = el; }}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleSlideMediaUpload(slide.id, file, 'image');
                                }}
                                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                className="hidden"
                              />
                              {slide.media_url ? (
                                <div className="relative group">
                                  <img
                                    src={slide.media_url}
                                    alt="Slide image"
                                    className="w-full h-48 object-cover rounded-lg border border-gray-200"
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => slideMediaInputRefs.current[slide.id]?.click()}
                                      className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                                    >
                                      Change
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => updateSlide(slide.id, { media_url: '' })}
                                      className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  onClick={() => uploadingSlideMedia !== slide.id && slideMediaInputRefs.current[slide.id]?.click()}
                                  className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors cursor-pointer ${uploadingSlideMedia === slide.id ? 'opacity-50 cursor-wait' : ''}`}
                                >
                                  {uploadingSlideMedia === slide.id ? (
                                    <Loader2 className="w-10 h-10 mx-auto text-primary-500 mb-2 animate-spin" />
                                  ) : (
                                    <Image className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                                  )}
                                  <p className="text-sm text-gray-500">
                                    {uploadingSlideMedia === slide.id ? 'Uploading...' : 'Click to upload image'}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 10MB</p>
                                </div>
                              )}
                            </div>
                          )}

                          {slide.slide_type === 'video' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
                              <input
                                type="url"
                                value={slide.video_embed_url}
                                onChange={(e) => updateSlide(slide.id, { video_embed_url: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                placeholder="YouTube or Vimeo URL"
                              />
                            </div>
                          )}

                          {slide.slide_type === 'pdf' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">PDF Document</label>
                              <input
                                type="file"
                                ref={(el) => { slideMediaInputRefs.current[`${slide.id}-pdf`] = el; }}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleSlideMediaUpload(slide.id, file, 'pdf');
                                }}
                                accept="application/pdf"
                                className="hidden"
                              />
                              {slide.media_url ? (
                                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <FileText className="w-10 h-10 text-orange-500 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">PDF uploaded</p>
                                    <a
                                      href={slide.media_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-primary-600 hover:underline"
                                    >
                                      View PDF
                                    </a>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => slideMediaInputRefs.current[`${slide.id}-pdf`]?.click()}
                                      className="px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50"
                                    >
                                      Change
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => updateSlide(slide.id, { media_url: '' })}
                                      className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  onClick={() => uploadingSlideMedia !== slide.id && slideMediaInputRefs.current[`${slide.id}-pdf`]?.click()}
                                  className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors cursor-pointer ${uploadingSlideMedia === slide.id ? 'opacity-50 cursor-wait' : ''}`}
                                >
                                  {uploadingSlideMedia === slide.id ? (
                                    <Loader2 className="w-10 h-10 mx-auto text-primary-500 mb-2 animate-spin" />
                                  ) : (
                                    <FileText className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                                  )}
                                  <p className="text-sm text-gray-500">
                                    {uploadingSlideMedia === slide.id ? 'Uploading...' : 'Click to upload PDF'}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">PDF up to 20MB</p>
                                </div>
                              )}
                            </div>
                          )}

                          {(slide.slide_type === 'quiz_single' || slide.slide_type === 'quiz_multiple' || slide.slide_type === 'quiz_truefalse') && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                                <textarea
                                  value={slide.quiz_question}
                                  onChange={(e) => updateSlide(slide.id, { quiz_question: e.target.value })}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                                  rows={2}
                                  placeholder="Enter your question..."
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Options {slide.slide_type === 'quiz_multiple' ? '(select all correct)' : '(select one correct)'}
                                </label>
                                <div className="space-y-2">
                                  {slide.quiz_options.map((option) => (
                                    <div key={option.id} className="flex items-center gap-2">
                                      <button
                                        onClick={() => {
                                          if (slide.slide_type === 'quiz_multiple') {
                                            updateQuizOption(slide.id, option.id, { is_correct: !option.is_correct });
                                          } else {
                                            // For single choice, unselect all others
                                            const updatedOptions = slide.quiz_options.map(o => ({
                                              ...o,
                                              is_correct: o.id === option.id,
                                            }));
                                            updateSlide(slide.id, { quiz_options: updatedOptions });
                                          }
                                        }}
                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                          option.is_correct
                                            ? 'border-green-500 bg-green-500 text-white'
                                            : 'border-gray-300 hover:border-green-400'
                                        }`}
                                      >
                                        {option.is_correct && <Check className="w-3.5 h-3.5" />}
                                      </button>
                                      <input
                                        type="text"
                                        value={option.text}
                                        onChange={(e) => updateQuizOption(slide.id, option.id, { text: e.target.value })}
                                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        placeholder="Option text"
                                        disabled={slide.slide_type === 'quiz_truefalse'}
                                      />
                                      {slide.slide_type !== 'quiz_truefalse' && slide.quiz_options.length > 2 && (
                                        <button
                                          onClick={() => removeQuizOption(slide.id, option.id)}
                                          className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {slide.slide_type !== 'quiz_truefalse' && (
                                  <button
                                    onClick={() => addQuizOption(slide.id)}
                                    className="mt-2 text-sm text-primary-600 hover:text-primary-700"
                                  >
                                    + Add option
                                  </button>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Explanation (optional)</label>
                                <textarea
                                  value={slide.quiz_explanation}
                                  onChange={(e) => updateSlide(slide.id, { quiz_explanation: e.target.value })}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                                  rows={2}
                                  placeholder="Explain the correct answer..."
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Close dropdown on outside click */}
      {showAddSlide && (
        <div className="fixed inset-0 z-0" onClick={() => setShowAddSlide(false)} />
      )}
    </div>
  );
}
